import { X, CalendarClock, Layers } from "lucide-react";
import React from "react";
import { YouMayAlsoLike } from "../../utils/Customer/products";
import { IgetUserCartResponse } from "../../types/Response/CustomerResponse";
import ProductSlider from "./ProductDetailComponent.tsx/ProductSlider";
import FreeShippingBar from "./ProductDetailComponent.tsx/FreeShippingBar";
import CommonButton from "../CommonButton";
import QuantityStepper from "./ProductDetailComponent.tsx/QuantityStepper";

// New Type Definition for Bundles based on your payload
export type CartBundleItem = {
  cartItemId: number;
  productVariantId: number;
  productName: string;
  variantName: string;
  imageUrl: string;
  quantity: number;
  unitPrice: number;
  total: number;
};

export type CartBundle = {
  bundleId: number;
  name: string;
  imageUrl: string;
  bundlePrice: number;
  originalPrice: number;
  savingAmount: number;
  items: CartBundleItem[];
  total: number;
};
type CartDrawerProps = {
  cartData?: IgetUserCartResponse;
  isOpen: boolean;
  onClose: () => void;
  onIncrement: (id: number) => void;
  onDecrement: (id: number) => void;
  onRemove: (id: number) => void;        // Dedicated for standard products
  onRemovePet: (id: number) => void;     // Dedicated for pet assets
  onRemoveService: (id: number) => void; // Dedicated for service bookings
  onRemoveBundle?: (id: number) => void;  // Dedicated for bundle package configurations
  onAddToCart: (product: any) => void;
  handleCheckoutClick?: () => void;
};

const HeaderDrawer: React.FC<CartDrawerProps> = ({
  cartData,
  isOpen,
  onClose,
  onDecrement,
  onIncrement,
  onRemove,
  onRemovePet,
  onRemoveService,
  onRemoveBundle,
  onAddToCart,
  handleCheckoutClick,
}) => {
  const total = cartData?.grandTotal || 0;

  // Calculate total item count including nested elements for accurate dynamic button tracking
  const hasItems =
    (cartData?.productCart?.items?.length || 0) +
    (cartData?.productCart?.bundles?.length || 0) +
    (cartData?.petCart?.items?.length || 0) +
    (cartData?.serviceCart?.items?.length || 0) > 0;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={onClose} />
      )}
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[400px] bg-white z-[9999] shadow-lg transform transition-transform duration-300 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <span className="font-bold text-lg text-logoHeading">Your Cart</span>

          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition"
          >
            <X size={20} className="text-iconColor" />
          </button>
        </div>
        <FreeShippingBar total={total} threshold={100} />

        {/* Cart Items */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {!hasItems ? (
            <p className="text-center text-gray-500 py-8">Your cart is empty</p>
          ) : (
            <>
              {/* Standard & Subscription Product Items Layer */}
              {cartData?.productCart?.items?.map((item) => (
                <div key={item.cartItemId} className="flex gap-3 border-b border-gray-50 pb-3 last:border-none">
                  <img
                    src={item.imageUrl}
                    className="w-16 h-16 object-cover rounded shrink-0 border border-gray-100"
                    alt={item.productName}
                  />

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-logoHeading truncate">{item.productName}</p>
                    <p className="text-xs text-gray-500">{item.variantName}</p>

                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-mainBrandColor font-bold">AED {item.total}</p>
                      {item.isSubscription && item.subscriptionDiscountPercent && item.subscriptionDiscountPercent > 0 ? (
                        <span className="text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                          Save {item.subscriptionDiscountPercent}%
                        </span>
                      ) : null}
                    </div>

                    {/* Subscription Metadata Badge */}
                    {item.isSubscription && (
                      <div className="flex items-center gap-1.5 mt-1.5 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-100 px-2 py-0.5 rounded w-fit">
                        <CalendarClock size={12} className="text-orange-500" />

                        {item.billingCycle === 'Custom' ? (
                          <span>
                            Autoship: Every {item.interval} {item.interval === 1 ? "Month" : "Months"}
                          </span>
                        ) : (
                          <span>
                            Autoship: {item.billingCycle || "Month"}
                          </span>
                        )}

                      </div>
                    )}

                    <div className="flex items-center gap-4 mt-2">
                      <QuantityStepper
                        qty={item.quantity}
                        onIncrement={() => onIncrement(item.cartItemId)}
                        onDecrement={() => onDecrement(item.cartItemId)}
                      />

                      <button
                        onClick={() => onRemove(item.cartItemId)}
                        className="text-red-500 text-sm hover:underline cursor-pointer"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Dynamic Bundle Package Layer */}
              {cartData?.productCart?.bundles?.map((bundle) => (
                <div key={bundle.bundleId} className="border border-purple-100 rounded-xl p-3 bg-purple-50/20 space-y-3">
                  <div className="flex gap-3 border-b border-purple-100/50 pb-2">
                    <img
                      src={bundle.imageUrl}
                      className="w-14 h-14 object-cover rounded shrink-0 border border-purple-200"
                      alt={bundle.name}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-0.5 rounded w-fit mb-0.5">
                        <Layers size={10} />
                        <span>Combo Bundle</span>
                      </div>
                      <p className="text-sm font-bold text-logoHeading truncate">{bundle.name}</p>
                      <p className="text-sm font-black text-mainBrandColor">AED {bundle.bundlePrice}</p>
                    </div>
                    {onRemoveBundle && (
                      <button
                        onClick={() => onRemoveBundle(bundle.bundleId)}
                        className="text-gray-400 hover:text-red-500 self-start p-1 transition"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Render Nested Bundle Product Elements */}
                  <div className="space-y-1.5 pl-2 border-l-2 border-purple-200">
                    {bundle.items?.map((bItem: CartBundleItem) => (
                      <div key={bItem.cartItemId} className="text-xs flex justify-between items-start gap-2 text-gray-600">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate text-gray-800 text-[11px]">{bItem.productName}</p>
                          <p className="text-[10px] text-gray-400">{bItem.variantName}</p>
                        </div>
                        <span className="shrink-0 font-semibold text-gray-400 text-[11px]">
                          x{bItem.quantity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Pet Cart Items Layer */}
              {cartData?.petCart?.items?.map((item) => (
                <div key={item.petCartItemId} className="flex gap-3 border-b border-gray-50 pb-3 last:border-none">
                  <img
                    src={item.productImage}
                    className="w-16 h-16 object-cover rounded shrink-0"
                    alt={item.petName}
                  />

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-logoHeading">{item.petName}</p>
                    <p className="text-xs text-gray-500">{item.breed}</p>
                    <span className="inline-block mt-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                      {item.purchaseType === "PartialBooking" ? "Partial Booking" : "Full Payment"}
                    </span>
                    <p className="text-mainBrandColor font-bold mt-1">AED {item.amount}</p>

                    <button
                      onClick={() => onRemovePet(item.petCartItemId)}
                      className="text-red-500 text-sm hover:underline cursor-pointer mt-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {/* Service Cart Bookings Layer */}
              {cartData?.serviceCart?.items?.map((item) => (
                <div key={item.cartItemId} className="border border-slate-100 rounded-xl p-3 bg-slate-50/40 space-y-1">
                  <p className="font-bold text-slate-800">{item.serviceName}</p>
                  {item.servicePackageName && (
                    <p className="text-xs text-slate-500">{item.servicePackageName}</p>
                  )}
                  {item.petName && (
                    <p className="text-xs text-slate-600 font-medium">Pet: {item.petName}</p>
                  )}
                  <p className="text-xs text-slate-500 ">{item.bookingDate?.split("T")[0]}</p>
                  <p className="text-xs text-slate-500 ">
                    {item.startTime} - {item.endTime}
                  </p>

                  <div className="flex items-center justify-between pt-1 border-t border-slate-100/60 mt-1">
                    <p className="text-mainBrandColor font-bold">AED {item.price}</p>

                    <button
                      onClick={() => onRemoveService(item.cartItemId)}
                      className="text-red-500 text-xs font-bold hover:underline cursor-pointer"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer Subtotal */}
        <div className="p-4 border-t bg-white">
          <p className="mb-2 font-bold text-red-800 text-lg">Total: AED {total.toFixed(2)}</p>
          <CommonButton
            text="Checkout"
            disabled={!hasItems}
            className="w-full bg-mainBrandColor text-white font-bold h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            rounded="md"
            variant="filled"
            onClick={handleCheckoutClick}
          />
        </div>
      </div>
    </>
  );
};

export default HeaderDrawer;